import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Award, Loader2, ArrowRight, Calendar, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Self-service Subscription page for both company and manager.
 * Renders relevant plans based on user.role and current subscription.
 */
export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    const [me, pl] = await Promise.all([
      api.get("/subscriptions/me"),
      api.get("/subscriptions/plans"),
    ]);
    setData(me.data);
    setPlans(pl.data?.plans);
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      await api.post("/subscriptions/subscribe", { plan });
      toast.success("Abbonamento attivato (mock)");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore attivazione");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!confirm("Confermi la cancellazione dell'abbonamento?")) return;
    setBusy("cancel");
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Abbonamento cancellato");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(null);
    }
  };

  if (!data || !plans) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>;
  }

  const isCompany = user?.role === "company";
  const myPlanKey = data.subscription?.plan;

  const availablePlans = Object.entries(plans).filter(([k, p]) => {
    if (p.is_quote_based) return false; // premium is quote
    if (isCompany) return k.startsWith("company_");
    return k.startsWith("manager_");
  });

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Abbonamento e piano</h1>
      <p className="text-sm text-slate-600 mb-6">Gestisci il tuo piano CXOORA. Pagamenti gestiti da Stripe (mocked in demo).</p>

      {/* Current */}
      <div className="cxo-card p-6 mb-6" data-testid="subscription-current">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Piano attuale</p>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-1">{data.plan_details?.name || "Free"}</h2>
            {data.subscription && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                <Calendar size={12} />
                Prossimo rinnovo: {new Date(data.subscription.next_billing_at || Date.now()).toLocaleDateString("it-IT")}
              </p>
            )}
          </div>
          <Badge className={
            data.current_tier === "agency_premium" ? "bg-[#B7791F]/10 text-[#B7791F] border-0"
            : data.current_tier === "marketplace_pro" ? "bg-[#2D6BFF]/10 text-[#2D6BFF] border-0"
            : "bg-slate-100 text-slate-600 border-0"
          }>
            {data.current_tier?.replace("_", " ").toUpperCase()}
          </Badge>
        </div>
        {data.subscription && data.subscription.monthly_price > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-700">Prezzo: <strong>€{data.subscription.monthly_price.toFixed(0)}/mese</strong></p>
            <Button variant="outline" onClick={cancel} disabled={busy === "cancel"} className="text-red-600 border-red-200 hover:border-red-300 gap-1.5" data-testid="cancel-subscription-btn">
              {busy === "cancel" && <Loader2 className="animate-spin" size={14} />}
              Annulla abbonamento
            </Button>
          </div>
        )}
      </div>

      {/* Available plans */}
      <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Piani disponibili</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availablePlans.map(([key, plan]) => {
          const isCurrent = key === myPlanKey;
          const Icon = key.includes("premium") ? Award : key.includes("pro") ? Sparkles : Check;
          return (
            <div key={key} className={`cxo-card p-5 ${isCurrent ? "ring-2 ring-[#2D6BFF]/30 bg-[#EAF0FF]/20" : ""}`} data-testid={`plan-card-${key}`}>
              <div className="flex items-center gap-2">
                <Icon size={18} className="text-[#2D6BFF]" />
                <h3 className="font-display font-bold text-slate-900">{plan.name}</h3>
                {isCurrent && <Badge className="bg-[#1F9D6A]/10 text-[#1F9D6A] border-0 ml-auto">Attuale</Badge>}
              </div>
              <p className="font-display text-2xl font-bold text-slate-900 mt-2">€{plan.monthly_price.toFixed(0)}<span className="text-sm text-slate-500"> / mese</span></p>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <Check size={12} className="mt-1 text-[#1F9D6A] shrink-0" /><span className="text-xs">{f}</span>
                  </li>
                ))}
              </ul>
              {!isCurrent && (
                <Button
                  onClick={() => subscribe(key)}
                  disabled={busy === key}
                  className="w-full mt-4 gap-1.5 bg-[#2D6BFF] hover:bg-[#1F57F2] text-white"
                  data-testid={`subscribe-${key}-btn`}
                >
                  {busy === key && <Loader2 className="animate-spin" size={14} />}
                  Attiva {plan.name} <ArrowRight size={14} />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Agency Premium info */}
      {isCompany && (
        <div className="cxo-card p-6 mt-6 bg-gradient-to-br from-[#FFF6E6] to-white border-l-4 border-l-[#B7791F]" data-testid="agency-premium-card">
          <div className="flex items-start gap-3">
            <Award size={24} className="text-[#B7791F]" />
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Vuoi Agency Premium?</h3>
              <p className="text-sm text-slate-700 mt-1">
                Per progetti complessi (≥ €3.000/mese) CXOORA offre il servizio Agency Premium con account manager dedicato, SLA contrattuali e selezione manager curata. Prezzo personalizzato dopo check-up.
              </p>
              <div className="mt-3 flex gap-2">
                <Link to="/contatti"><Button className="bg-[#B7791F] hover:bg-[#8B5E12] text-white gap-1.5"><AlertCircle size={14} /> Parla con CXOORA</Button></Link>
                <Link to="/check-up"><Button variant="outline">Fai il check-up</Button></Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 text-center text-xs text-slate-500">
        💳 Stripe MOCKED in demo. In produzione i pagamenti saranno gestiti tramite Stripe Subscriptions.
      </div>
    </div>
  );
}
